import React, { useEffect, useState } from 'react'
import { useValidation } from 'hooks'
import { makeStyles } from '@material-ui/core/styles'
import style from './style'
import Icon from '../Icon/Icon'
import Typography from '../Typography/Typography'
import IconButton from '../IconButton/IconButton'
import CommonInputType from '../../helpers/types/CommonInput.interface'

const useStyles = makeStyles(style)

const getHelperText = (hText) => {
    if (hText && typeof hText === 'string') {
        return <>
            <Icon color='neutral-400' name='notification-alert' size={16} className='helper-text-icon' />
            <Typography tag='span' variant='body4' color='neutral-400' className='helper-text'>{hText}</Typography>
        </>
    }

    return hText
}

const renderHelperText = (hText, stls) => {
    if (!hText) return ''
    return <div className={stls.inputHelperText} data-testid='helper-text'>
        {getHelperText(hText)}
    </div>
}

interface PropsInterface extends CommonInputType {
    children: string | JSX.Element | JSX.Element[];
    label?: string | false
}

/**
 *
 */
const CommonInput = ({
    value,
    rules,
    isValid,
    valueHasBeenChanged,
    valueHasBeenChangedAtLeastOnce,
    validateOn = 'change',
    errorsToShow = 1,
    children,
    helperText,
    onClear,
    style,
    disabled,
    clearable = false,
    size,
    fullWidth = false,
    className,
    label = false,
    leadingIcon,
    trailingIcon,
    maxLength,
    onClickLeadingIcon,
    onClickTrailingIcon,
    id,
    'data-testid': dataTestID = ''
}: PropsInterface): JSX.Element => {
    const styles = useStyles({
        disabled,
        maxLength,
        hasLeadingIconAction: !!onClickLeadingIcon,
        hasTrailingIconAction: !!onClickTrailingIcon
    })

    const [originalValue, setOriginalValue] = useState<any>(null)
    const [hasValueBeenChanged, setHasValueBeenChanged] = useState<boolean>(false)
    const [inputState, setInputState] = useState<'focus' | 'blur' | 'unknown'>('unknown')

    const [errors] = useValidation(
        value,
        rules,
        isValid,
        validateOn,
        hasValueBeenChanged,
        inputState
    )

    const dataTestId = `${dataTestID}-common-input`
    const wrapperClasses = [
        className,
        'input-wrapper',
        errors && errors.length ? 'has-error' : '',
        leadingIcon && 'has-leading-icon',
        trailingIcon && 'has-trailing-icon',
        size || ''
    ].filter((x) => x).join(' ')

    useEffect(() => {
        if (originalValue === null) {
            setOriginalValue(value)
        } else {
            setHasValueBeenChanged(true)
            valueHasBeenChanged && valueHasBeenChanged(originalValue !== value)
        }
    }, [value])

    useEffect(() => {
        if (originalValue !== null) valueHasBeenChangedAtLeastOnce && valueHasBeenChangedAtLeastOnce(hasValueBeenChanged)
    }, [hasValueBeenChanged])

    const userMayClearTheField = value && !disabled && clearable && onClear

    return (
        <div
            className={`
                foundations-common-input
                ${value ? 'foundations-common-input--has-value' : ''}
                ${styles.commonInput}
                ${fullWidth && 'common-input--full-width'}
                ${userMayClearTheField && 'common-input-clearable'}
                ${className || ''}`
            }
            data-testid={dataTestId}
        >
            <div
                className={wrapperClasses}
                style={style}
                onFocus={() => setInputState('focus')}
                onBlur={() => setInputState('blur')}
            >
                {label && <label className='label-in-border' htmlFor={id}>{label}</label>}
                {
                    leadingIcon &&
                    <Icon
                        onMouseDown={() => !disabled && onClickLeadingIcon && onClickLeadingIcon()}
                        color={disabled ? 'neutral-300' : 'neutral-600'}
                        name={leadingIcon}
                        size={20}
                        className='leading-icon'
                        data-testid={`${dataTestID}-leading-icon`}
                    />
                }
                {children}
                {userMayClearTheField && <IconButton
                    className='clear-input-button'
                    size='small'
                    variant='icon'
                    color='neutral'
                    name='close'
                    onMouseDown={onClear}
                    type='button'
                    data-testid='clear-field-button'
                />}
                {
                    trailingIcon &&
                    <Icon
                        name={trailingIcon}
                        onMouseDown={() => !disabled && onClickTrailingIcon && onClickTrailingIcon()}
                        color={disabled ? 'neutral-300' : 'neutral-600'}
                        size={20}
                        className='trailing-icon'
                        data-testid={`${dataTestID}-trailing-icon`}
                    />
                }
            </div>

            {errors?.length
                ? (<Typography variant='helperText' color='destructive-500' data-testid='validation-errors'>
                    {errors.slice(0, errorsToShow).map((x, i) => <div className={styles.validationError} key={`validation-error-${i}`}>{x}</div>)}
                </Typography>)
                : <></>}

            {renderHelperText(helperText, styles)}
        </div>
    )
}

export default CommonInput
